// src/pages/SingleProperty.tsx
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { db } from '../../firebaseConfig';
import { doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import Navbar from '../../components/navbar/navbar';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import { Accordion } from 'react-bootstrap';
import ImageGallery from '../../components/ImageGallery';
import PriceDisplay from '../../components/PriceDisplay';
import CurrencyToggle from '../../components/CurrencyToggle';
import Breadcrumb from '../../components/Breadcrumb';
import ContactButtons from '../../components/ContactButtons';
import PropertyContactForm from '../../components/PropertyContactForm';
import { useUser } from '../../context/UserContext';
import { usePropertySEO } from '../../hooks/useSEO';
import 'bootstrap/dist/css/bootstrap.min.css';

import { PropertyPageSkeleton } from '../../components/loading-states';

import bed from '../../assets/img/bed.svg';
import bathtub from '../../assets/img/bathtub.svg';
import move from '../../assets/img/move.svg';

// Sprint 8 (F1-011): cache en memoria de propiedades similares por ID.
// Evita re-query si el usuario vuelve a la misma propiedad en la misma sesión.
// TTL 5 min; más que suficiente para navegación usual. Se limpia al F5.
const SIMILARES_CACHE = new Map<string, { at: number; data: any[] }>();
const SIMILARES_TTL_MS = 5 * 60 * 1000;

export default function SingleProperty() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { user } = useUser();
  const [propiedad, setPropiedad] = useState<any>(null);
  const [inmobiliaria, setInmobiliaria] = useState<any>(null);
  const [asesores, setAsesores] = useState<any[]>([]);
  const [asociados, setAsociados] = useState<any[]>([]);
  const [proyecto, setProyecto] = useState<any>(null);
  const [propiedadesSimilares, setPropiedadesSimilares] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // SEO dinámico para la propiedad
  usePropertySEO(propiedad);

  const badgeColors: Record<string, string> = {
    Disponible: 'success',
    Pendiente: 'warning',
    Vendida: 'danger',
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        const propRef = doc(db, 'propiedades', id);
        const propSnap = await getDoc(propRef);

        if (propSnap.exists()) {
          const propData = propSnap.data();
          setPropiedad(propData);
          
          console.log('Datos de propiedad cargados:', propData); // Debug

          // ✅ CORREGIDO: Buscar inmobiliaria usando doc.id
          const inmobiliariaDocId = propData.inmobiliaria_doc_id;

          if (inmobiliariaDocId) {
            const inmoDocRef = doc(db, 'inmobiliaria', inmobiliariaDocId);
            const inmoSnap = await getDoc(inmoDocRef);
            if (inmoSnap.exists()) {
              const inmo = { id: inmoSnap.id, ...inmoSnap.data() };
              setInmobiliaria(inmo);
            }
          }

          // ✅ CORREGIDO: Buscar asesores usando doc.ids
          const asesorDocIds = propData.asesor_doc_ids;
          if (asesorDocIds?.length > 0) {
            // Cargar asesores por doc.id
            const asesoresData: any[] = [];
            const asociadosData: any[] = [];

            for (const docId of asesorDocIds) {
              // Intentar buscar en asesores
              try {
                const asesorRef = doc(db, 'asesor', docId);
                const asesorSnap = await getDoc(asesorRef);
                if (asesorSnap.exists()) {
                  asesoresData.push({ id: asesorSnap.id, ...asesorSnap.data() });
                }
              } catch {}

              // Intentar buscar en asociados
              try {
                const asociadoRef = doc(db, 'asociado', docId);
                const asociadoSnap = await getDoc(asociadoRef);
                if (asociadoSnap.exists()) {
                  asociadosData.push({ id: asociadoSnap.id, ...asociadoSnap.data() });
                }
              } catch {}
            }

            setAsesores(asesoresData);
            setAsociados(asociadosData);
          }

          if (propData.proyecto_id) {
            const proyectoQuery = query(collection(db, 'proyecto'), where('proyecto_id', '==', propData.proyecto_id));
            const proyectoSnap = await getDocs(proyectoQuery);
            if (!proyectoSnap.empty) setProyecto(proyectoSnap.docs[0].data());
          }

          // ✅ Cargar propiedades similares
          if (propData.tipo_propiedad && propData.precio_actual_usd) {
            const precioMin = propData.precio_actual_usd * 0.7;
            const precioMax = propData.precio_actual_usd * 1.3;

            const similaresQuery = query(
              collection(db, 'propiedades'),
              where('tipo_propiedad', '==', propData.tipo_propiedad),
              where('estado', '==', 'A'),
              where('precio_actual_usd', '>=', precioMin),
              where('precio_actual_usd', '<=', precioMax),
              limit(4)
            );

            // Sprint 8 (F1-011): cache por ID de propiedad con TTL 5min
            const cacheKey = `${id}|${propData.tipo_propiedad}|${Math.round(propData.precio_actual_usd)}`;
            const cached = SIMILARES_CACHE.get(cacheKey);
            if (cached && Date.now() - cached.at < SIMILARES_TTL_MS) {
              setPropiedadesSimilares(cached.data);
            } else {
              try {
                const similaresSnap = await getDocs(similaresQuery);
                const similares = similaresSnap.docs
                  .map(doc => ({ docId: doc.id, ...doc.data() }))
                  .filter(prop => prop.docId !== id)
                  .slice(0, 3);

                SIMILARES_CACHE.set(cacheKey, { at: Date.now(), data: similares });
                setPropiedadesSimilares(similares);
              } catch (error) {
                if (process.env.NODE_ENV === 'development') {
                  console.error('Error cargando propiedades similares:', error);
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error cargando la propiedad:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading) return (
    <>
      {user ? (
        <UserNav nombreUsuario={user.nombre} nombreInmobiliaria={user.inmobiliaria_nombre} />
      ) : (
        <Navbar transparent={false} />
      )}
      <PropertyPageSkeleton />
      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
  if (!propiedad) return (
    <div className="text-center py-5">
      <i className="fa-solid fa-house-circle-xmark fa-3x text-muted mb-3"></i>
      <h5 className="text-muted">No se encontró la propiedad</h5>
      <Link to="/grid" className="btn btn-primary mt-3">Ver propiedades</Link>
    </div>
  );

  return (
    <>
      {/* 🎯 Navegación consciente del usuario */}
      {user ? (
        <UserNav
          nombreUsuario={user.nombre}
          nombreInmobiliaria={user.inmobiliaria_nombre}
        />
      ) : (
        <Navbar transparent={false} />
      )}

      {/* 🧭 Breadcrumb de navegación */}
      <div className="container mt-4">
        <Breadcrumb
          items={[
            { label: 'Inicio', path: '/' },
            { label: 'Propiedades', path: '/classical-layout-with-sidebar' },
            { label: propiedad?.titulo || propiedad?.list_number || 'Detalle' }
          ]}
        />
      </div>

      {/* 🖼 Galería */}
      <div className="featured_slick_gallery gray py-4">
        <div className="container">
          {propiedad.fotos && propiedad.fotos.length > 0 ? (
            <ImageGallery
              images={propiedad.fotos}
              showThumbnails={true}
              thumbnailSize="lg"
              lightbox={true}
              className="property-main-gallery"
            />
          ) : (
            <div className="text-center bg-light p-5 rounded">
              <i className="fa fa-image fa-3x text-muted mb-3"></i>
              <p className="text-muted">Sin imágenes disponibles</p>
            </div>
          )}
        </div>
      </div>

      {/* 🏠 Detalles Principales */}
      <section className="gray-simple">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 col-md-12">
              <div className="property_block_wrap style-2 p-4">

                {/* === HEADER: Badges compactos + Título === */}
                <div className="d-flex flex-wrap gap-2 mb-2">
                  {propiedad.tipo_propiedad && (
                    <span className="badge bg-primary">{propiedad.tipo_propiedad}</span>
                  )}
                  {propiedad.estado === 'A' && (
                    <span className="badge bg-success">Disponible</span>
                  )}
                  {propiedad.zona && (
                    <span className="badge bg-secondary">
                      <i className="fas fa-map-marker-alt me-1"></i>{propiedad.zona}
                    </span>
                  )}
                  {propiedad.comunidad && propiedad.comunidad !== propiedad.zona && (
                    <span className="badge bg-secondary">{propiedad.comunidad}</span>
                  )}
                </div>

                <h2 className="mb-2" style={{ fontSize: '1.5rem', fontWeight: 600 }}>
                  {propiedad.titulo || propiedad.list_number}
                </h2>

                {/* === UBICACIÓN === */}
                <p className="text-muted mb-3">
                  <i className="fas fa-map-marker-alt me-2 text-primary"></i>
                  {[
                    propiedad.zona || propiedad.direccion?.area,
                    propiedad.comunidad && propiedad.comunidad !== propiedad.zona ? propiedad.comunidad : null,
                    propiedad.ciudad || propiedad.direccion?.ciudad
                  ].filter(Boolean).join(', ') || 'Riviera Nayarit'}
                </p>

                {/* === PRECIO === */}
                <div className="d-flex align-items-center flex-wrap gap-3 mb-4 pb-3 border-bottom">
                  <div>
                    {(propiedad.precio_actual_mxn > 0 || propiedad.precio_actual_usd > 0) ? (
                      <PriceDisplay
                        priceMXN={propiedad.precio_actual_mxn}
                        priceUSD={propiedad.precio_actual_usd}
                        agencyExchangeRate={propiedad.tipo_cambio_agencia}
                        showBoth={false}
                        showExchangeRate={false}
                      />
                    ) : (
                      <span className="h4 text-muted">Precio por consultar</span>
                    )}
                  </div>
                  <CurrencyToggle />
                </div>

                {/* === FEATURES STRIP === */}
                <div className="row text-center g-2 mb-4">
                  <div className="col">
                    <div className="bg-light rounded p-3">
                      <i className="fas fa-bed text-primary d-block mb-1" style={{ fontSize: '1.2rem' }}></i>
                      <span className="fw-bold d-block">{propiedad.recamaras || 0}</span>
                      <small className="text-muted">Recámaras</small>
                    </div>
                  </div>
                  <div className="col">
                    <div className="bg-light rounded p-3">
                      <i className="fas fa-bath text-primary d-block mb-1" style={{ fontSize: '1.2rem' }}></i>
                      <span className="fw-bold d-block">{(propiedad.banos_completos || 0) + (propiedad.medios_banos || 0)}</span>
                      <small className="text-muted">Baños</small>
                    </div>
                  </div>
                  <div className="col">
                    <div className="bg-light rounded p-3">
                      <i className="fas fa-ruler-combined text-primary d-block mb-1" style={{ fontSize: '1.2rem' }}></i>
                      <span className="fw-bold d-block">{propiedad.m2_total || propiedad.m2_lote || 0}</span>
                      <small className="text-muted">m²</small>
                    </div>
                  </div>
                  {propiedad.estacionamiento > 0 && (
                    <div className="col">
                      <div className="bg-light rounded p-3">
                        <i className="fas fa-car text-primary d-block mb-1" style={{ fontSize: '1.2rem' }}></i>
                        <span className="fw-bold d-block">{propiedad.estacionamiento}</span>
                        <small className="text-muted">{propiedad.tipo_estacionamiento || 'Parking'}</small>
                      </div>
                    </div>
                  )}
                  {propiedad.anos_construccion && (
                    <div className="col">
                      <div className="bg-light rounded p-3">
                        <i className="fas fa-calendar text-primary d-block mb-1" style={{ fontSize: '1.2rem' }}></i>
                        <span className="fw-bold d-block">{propiedad.anos_construccion}</span>
                        <small className="text-muted">Año</small>
                      </div>
                    </div>
                  )}
                </div>

                {/* === TOUR VIRTUAL === */}
                {propiedad.tour_virtual_url && (
                  <div className="mb-4">
                    <a
                      href={propiedad.tour_virtual_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-primary"
                    >
                      <i className="fas fa-vr-cardboard me-2"></i>
                      Tour Virtual
                    </a>
                  </div>
                )}

                {/* === DESCRIPCIÓN (visible, sin accordion) === */}
                <div className="mb-4">
                  <h5 className="mb-3">Descripción</h5>
                  {propiedad.descripcion ? (
                    (() => {
                      const raw = propiedad.descripcion as string;
                      const bilingualMatch = raw.match(/<?\s*(?:SPANISH|ESPAÑOL|EN ESPAÑOL|SPANISH BELOW)\s*>?\s*/i);
                      let enText = raw;
                      let esText = '';
                      if (bilingualMatch && bilingualMatch.index !== undefined) {
                        enText = raw.substring(0, bilingualMatch.index).trim();
                        esText = raw.substring(bilingualMatch.index + bilingualMatch[0].length).trim();
                      }
                      const clean = (s: string) => s.replace(/<\/?[^>]+(>|$)/g, '').trim();
                      const enClean = clean(enText);
                      const esClean = clean(esText);

                      return (
                        <>
                          {enClean && (
                            <div className="mb-3">
                              {esClean && <span className="badge bg-secondary mb-2">English</span>}
                              <p className="text-muted" style={{ whiteSpace: 'pre-line', lineHeight: 1.7 }}>{enClean}</p>
                            </div>
                          )}
                          {esClean && (
                            <div>
                              <span className="badge bg-primary mb-2">Español</span>
                              <p className="text-muted" style={{ whiteSpace: 'pre-line', lineHeight: 1.7 }}>{esClean}</p>
                            </div>
                          )}
                          {!enClean && !esClean && (
                            <p className="text-muted">Descripción no disponible</p>
                          )}
                        </>
                      );
                    })()
                  ) : (
                    <p className="text-muted">Descripción no disponible</p>
                  )}
                </div>

                {/* === DETALLES EN ACCORDION === */}
                <Accordion defaultActiveKey="0" className="mt-2">
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>Características</Accordion.Header>
                    <Accordion.Body>
                      <div className="row">
                        <div className="col-md-6">
                          <ul className="list-unstyled mb-0">
                            <li className="mb-2"><strong>Tipo:</strong> {propiedad.tipo_propiedad || 'No especificado'}</li>
                            <li className="mb-2"><strong>Recámaras:</strong> {propiedad.recamaras || 0}</li>
                            <li className="mb-2"><strong>Baños completos:</strong> {propiedad.banos_completos || 0}</li>
                            {propiedad.medios_banos > 0 && (
                              <li className="mb-2"><strong>Medios baños:</strong> {propiedad.medios_banos}</li>
                            )}
                            {propiedad.estilo && (
                              <li className="mb-2"><strong>Estilo:</strong> {propiedad.estilo}</li>
                            )}
                          </ul>
                        </div>
                        <div className="col-md-6">
                          <ul className="list-unstyled mb-0">
                            {propiedad.m2_total > 0 && (
                              <li className="mb-2"><strong>Construcción:</strong> {propiedad.m2_total} m²</li>
                            )}
                            {propiedad.m2_lote > 0 && (
                              <li className="mb-2"><strong>Terreno:</strong> {propiedad.m2_lote} m²</li>
                            )}
                            {propiedad.sqft > 0 && (
                              <li className="mb-2"><strong>Superficie:</strong> {propiedad.sqft} sqft</li>
                            )}
                            {propiedad.anos_construccion && (
                              <li className="mb-2"><strong>Año construcción:</strong> {propiedad.anos_construccion}</li>
                            )}
                            {propiedad.pisos && (
                              <li className="mb-2"><strong>Pisos:</strong> {propiedad.pisos}</li>
                            )}
                            {propiedad.estacionamiento > 0 && (
                              <li className="mb-2"><strong>Estacionamiento:</strong> {propiedad.estacionamiento} {propiedad.tipo_estacionamiento || ''}</li>
                            )}
                            {propiedad.dias_en_mercado > 0 && (
                              <li className="mb-2"><strong>Días en mercado:</strong> {propiedad.dias_en_mercado}</li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>

                  {propiedad.amenidades && propiedad.amenidades.length > 0 && (
                    <Accordion.Item eventKey="1">
                      <Accordion.Header>Amenidades</Accordion.Header>
                      <Accordion.Body>
                        <div className="row">
                          {propiedad.amenidades.map((amenidad: string, i: number) => (
                            <div key={i} className="col-md-6 mb-2">
                              <i className="fa-solid fa-check text-success me-2"></i>
                              {amenidad}
                            </div>
                          ))}
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                  <Accordion.Item eventKey="2">
                    <Accordion.Header>Ubicación</Accordion.Header>
                    <Accordion.Body>
                      {propiedad.direccion && (
                        <div className="mb-3">
                          <p className="text-muted mb-0">
                            {[
                              [propiedad.direccion.calle, propiedad.direccion.numero].filter(Boolean).join(' '),
                              propiedad.direccion.unidad ? `Unidad ${propiedad.direccion.unidad}` : null,
                              propiedad.direccion.area,
                              propiedad.direccion.zona,
                              propiedad.direccion.estado
                            ].filter(Boolean).join(', ')}
                          </p>
                        </div>
                      )}

                      {propiedad.direccion && (
                        <>
                          <div className="map-container mb-3" style={{ height: '350px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #eee' }}>
                            <iframe
                              width="100%"
                              height="100%"
                              frameBorder="0"
                              style={{ border: 0 }}
                              src={`https://www.google.com/maps?q=${encodeURIComponent(
                                [propiedad.direccion.calle, propiedad.direccion.numero, propiedad.direccion.area, propiedad.direccion.estado].filter(Boolean).join(', ')
                              )}&output=embed`}
                              allowFullScreen
                              loading="lazy"
                              referrerPolicy="no-referrer-when-downgrade"
                            ></iframe>
                          </div>

                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                              [propiedad.direccion.calle, propiedad.direccion.numero, propiedad.direccion.area, propiedad.direccion.estado].filter(Boolean).join(', ')
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline-secondary btn-sm"
                          >
                            <i className="fa-brands fa-google me-2"></i>
                            Abrir en Google Maps
                          </a>
                        </>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>

              </div>
            </div>

            <div className="col-lg-4 col-md-12">
              {/* Formulario de contacto */}
              {id && (
                <div className="sticky-top" style={{ top: '100px', zIndex: 10 }}>
                  <PropertyContactForm
                    propertyId={id}
                    propertyName={propiedad.titulo || propiedad.list_number || 'Propiedad'}
                    asesorIds={propiedad.asesor_doc_ids || []}
                    inmobiliariaId={propiedad.inmobiliaria_doc_id}
                    variant="card"
                  />

                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 🏘️ Sección de Propiedades Similares */}
      {propiedadesSimilares.length > 0 && (
        <section className="bg-light py-5">
          <div className="container">
            <div className="row mb-4">
              <div className="col-12">
                <h3 className="text-center mb-2">
                  <i className="fas fa-home me-2 text-primary"></i>
                  Propiedades Similares
                </h3>
                <p className="text-center text-muted">
                  Otras propiedades que podrían interesarte
                </p>
              </div>
            </div>

            <div className="row g-4">
              {propiedadesSimilares.map((prop, index) => (
                <div className="col-lg-4 col-md-6 col-sm-12" key={`similar-${prop.docId}-${index}`}>
                  <div className="card h-100 shadow-sm hover-shadow-lg transition">
                    {/* Imagen principal */}
                    <div className="position-relative" style={{ height: '200px', overflow: 'hidden' }}>
                      {prop.fotos && prop.fotos.length > 0 ? (
                        <img
                          src={prop.fotos[0]}
                          alt={prop.titulo || 'Propiedad'}
                          className="card-img-top h-100 w-100"
                          style={{ objectFit: 'cover' }}
                        />
                      ) : (
                        <div className="bg-secondary h-100 d-flex align-items-center justify-content-center">
                          <i className="fa fa-image fa-3x text-white-50"></i>
                        </div>
                      )}

                      {/* Badge de tipo */}
                      <span className="badge bg-primary position-absolute top-0 start-0 m-2">
                        {prop.tipo_propiedad || 'Propiedad'}
                      </span>

                      {/* Badge de estado */}
                      {prop.estado === 'A' && (
                        <span className="badge bg-success position-absolute top-0 end-0 m-2">
                          Disponible
                        </span>
                      )}
                    </div>

                    {/* Contenido */}
                    <div className="card-body">
                      <h5 className="card-title text-truncate mb-2" title={prop.titulo}>
                        {prop.titulo || prop.list_number || 'Propiedad'}
                      </h5>

                      <p className="text-muted small mb-2">
                        <i className="fas fa-map-marker-alt me-1"></i>
                        {prop.zona || prop.area || prop.direccion?.area || prop.ciudad || 'Ubicación no especificada'}
                      </p>

                      {/* Precio */}
                      <div className="mb-3">
                        {(prop.precio_actual_mxn > 0 || prop.precio_actual_usd > 0) ? (
                          <PriceDisplay
                            priceMXN={prop.precio_actual_mxn}
                            priceUSD={prop.precio_actual_usd}
                            agencyExchangeRate={prop.tipo_cambio_agencia}
                            className="h5 mb-0 text-primary fw-bold"
                            showExchangeRate={false}
                          />
                        ) : (
                          <p className="text-muted mb-0">Precio por consultar</p>
                        )}
                      </div>

                      {/* Características rápidas */}
                      <div className="row text-center mb-3">
                        <div className="col-4">
                          <small className="text-muted d-block">
                            <i className="fas fa-bed text-primary"></i>
                          </small>
                          <small className="fw-bold">{prop.recamaras || 0}</small>
                          <small className="text-muted d-block tiny">Rec.</small>
                        </div>
                        <div className="col-4">
                          <small className="text-muted d-block">
                            <i className="fas fa-bath text-primary"></i>
                          </small>
                          <small className="fw-bold">{prop.banos_completos || 0}</small>
                          <small className="text-muted d-block tiny">Baños</small>
                        </div>
                        <div className="col-4">
                          <small className="text-muted d-block">
                            <i className="fas fa-ruler-combined text-primary"></i>
                          </small>
                          <small className="fw-bold">{prop.m2_total || prop.m2_lote || 0}</small>
                          <small className="text-muted d-block tiny">m²</small>
                        </div>
                      </div>

                      {/* Botón ver más */}
                      <Link
                        to={`/single-property-1/${prop.docId}`}
                        className="btn btn-outline-primary w-100"
                      >
                        <i className="fas fa-eye me-2"></i>
                        Ver Detalles
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Botón ver todas */}
            <div className="row mt-4">
              <div className="col-12 text-center">
                <Link
                  to="/classical-layout-with-sidebar"
                  className="btn btn-primary btn-lg"
                >
                  <i className="fas fa-search me-2"></i>
                  Ver Todas las Propiedades
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
